/**
 * Flow A (first session) as a step machine derived from persisted progress, so a reload
 * mid-tutorial resumes at the right step. Also routes Flow D (returning player).
 *
 *   l1 → l2 → l3 → mapReveal → firstUpgrade (free counter) → l4 → l5 → secondUpgrade
 *      → nameShop → savePrompt → done            (save prompt asked again after level 10)
 */
import { cityShops } from '../config/content.js';
import { highestCompleted, upgradeCount } from './progress.js';
import type { PlayerState } from './state.js';

export type FsStep =
  | 'l1'
  | 'l2'
  | 'l3'
  | 'mapReveal'
  | 'firstUpgrade'
  | 'l4'
  | 'l5'
  | 'secondUpgrade'
  | 'nameShop'
  | 'savePrompt'
  | 'done';

/** Persisted markers (flags.tutorial). */
export const MARK = {
  mapRevealed: 'map_reveal',
  shopNamed: 'shop_named',
  savePrompt: 'save_prompt',
  savePrompt10: 'save_prompt_l10',
} as const;

export function hasMark(s: PlayerState, m: string): boolean {
  return s.flags.tutorial.includes(m);
}

export function mark(s: PlayerState, m: string): boolean {
  if (hasMark(s, m)) return false;
  s.flags.tutorial.push(m);
  return true;
}

function firstShopId(s: PlayerState): string {
  return cityShops(s.city)[0]!.id;
}

export function currentStep(s: PlayerState): FsStep {
  const done = highestCompleted(s);
  if (done < 3) return (['l1', 'l2', 'l3'] as const)[done]!;
  if (!hasMark(s, MARK.mapRevealed)) return 'mapReveal';
  const ups = upgradeCount(s, firstShopId(s));
  if (ups < 1) return 'firstUpgrade';
  if (ups < 2) return done < 4 ? 'l4' : done < 5 ? 'l5' : 'secondUpgrade';
  if (!hasMark(s, MARK.shopNamed) && !s.shopName) return 'nameShop';
  if (!hasMark(s, MARK.savePrompt)) return 'savePrompt';
  return 'done';
}

/** Levels 1–3 play back to back with no menus (Flow A C–E). */
export function inTutorialRun(s: PlayerState): boolean {
  const st = currentStep(s);
  return st === 'l1' || st === 'l2' || st === 'l3';
}

export type Route = { to: 'play'; level: number } | { to: 'map'; reveal: boolean };

/** Where the app opens: straight into the level during Flow A, the street map otherwise (Flow D). */
export function bootRoute(s: PlayerState): Route {
  if (inTutorialRun(s)) return { to: 'play', level: highestCompleted(s) + 1 };
  return { to: 'map', reveal: currentStep(s) === 'mapReveal' };
}

/** After Continue on the win screen. */
export function routeAfterWin(s: PlayerState): Route {
  if (inTutorialRun(s)) return { to: 'play', level: highestCompleted(s) + 1 };
  return { to: 'map', reveal: currentStep(s) === 'mapReveal' };
}

export type MapPrompt = 'nameShop' | 'savePrompt' | 'savePrompt10';

/** Prompts the map shows (one at a time) once their step is reached. */
export function pendingMapPrompt(s: PlayerState): MapPrompt | null {
  const st = currentStep(s);
  if (st === 'nameShop') return 'nameShop';
  if (st === 'savePrompt') return 'savePrompt';
  if (st === 'done' && highestCompleted(s) >= 10 && !hasMark(s, MARK.savePrompt10)) return 'savePrompt10';
  return null;
}

/** Tutorial hints on gameplay (hand pointer on L1 only). */
export function showHandPointer(s: PlayerState, levelNumber: number): boolean {
  return levelNumber === 1 && highestCompleted(s) === 0;
}
